import { useEffect, useState } from "react";
import { pushApi } from "@/lib/api";
import { BellRinging, BellSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Push notification opt-in button.
 *
 * Placement: ProfileView. Hides itself entirely when:
 *   - the browser doesn't support push, or
 *   - VAPID keys aren't configured on the server.
 *
 * Subscribe flow:
 *   1. Ask Notification.permission
 *   2. registration.pushManager.subscribe(applicationServerKey=VAPID_PUBLIC_KEY)
 *   3. POST the subscription to /api/push/subscribe
 */

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map((c) => c.charCodeAt(0)));
}

export default function PushNotificationToggle() {
  const [supported, setSupported] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [publicKey, setPublicKey] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const sup = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(sup);
    if (!sup) return;
    pushApi
      .publicKey()
      .then(({ data }) => {
        setConfigured(Boolean(data?.configured));
        setPublicKey(data?.publicKey || "");
      })
      .catch(() => {});
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setEnabled(Boolean(sub));
    });
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notification permission denied"); return; }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }
      const json = sub.toJSON();
      await pushApi.subscribe({ endpoint: json.endpoint, keys: json.keys });
      setEnabled(true);
      toast.success("Push notifications enabled");
    } catch (e) {
      toast.error("Could not enable push: " + (e?.message || ""));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try { await pushApi.unsubscribe(sub.endpoint); } catch (_) {}
        await sub.unsubscribe();
      }
      setEnabled(false);
      toast.success("Push notifications disabled");
    } catch (e) {
      toast.error("Could not disable push");
    } finally {
      setBusy(false);
    }
  };

  if (!supported || !configured || !publicKey) return null;

  return (
    <button
      onClick={enabled ? disable : enable}
      disabled={busy}
      data-testid="push-toggle-btn"
      className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border ${enabled ? "bg-primary/5 border-primary/30" : "bg-card border-border"} active:scale-95 transition-transform disabled:opacity-60`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${enabled ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>
          {enabled ? <BellRinging className="w-5 h-5" weight="fill" /> : <BellSlash className="w-5 h-5" />}
        </div>
        <div className="text-left">
          <p className="font-medium text-sm">Push notifications</p>
          <p className="text-xs text-muted-foreground">
            {enabled ? "On — you'll get instant alerts." : "Off — get reminders for new bereavements."}
          </p>
        </div>
      </div>
      <span className={`text-xs font-semibold ${enabled ? "text-primary" : "text-muted-foreground"}`}>
        {busy ? "…" : enabled ? "On" : "Enable"}
      </span>
    </button>
  );
}
